
    import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
    import AccountClient from '../services/AccountClient';
    import ChangePasswordDto from '@/dtos/ChangePasswordDto';
    import SecondFactorClient from '../services/SecondFactorClient';
    import { useToast } from "primevue/usetoast";
    import { useRouter } from 'vue-router';
    import { required } from '@vuelidate/validators';
    import useVuelidate from "@vuelidate/core";
    import UErrorContainer from "@/components/common/UErrorContainer.vue";
    import UErrorItem from "@/components/common/UErrorItem.vue";
    import { useStore } from '../store';
    import UStrongPassword from '@/components/common/UStrongPassword.vue';
    import { useI18n } from 'vue-i18n';
    import { useHead } from '@vueuse/head';
    import AccountDto from '../dtos/AccountDto';
    import SecondFactorDto from '@/dtos/SecondFactorDto';
    import VerificationPrompt from '@/components/VerificationPrompt.vue';
    import ChangePasswordValidateDto from '../dtos/ChangePasswordValidateDto';
    import { useReCaptcha } from 'vue-recaptcha-v3';
    import {
        DEFAULT_MIN_PASSWORD_LENGTH,
        DEFAULT_MIN_PASSWORD_SCORE,
        SERVICE_ACCOUNT_MIN_PASSWORD_LENGTH
    } from '../config/passwordPolicy';
    
    export default defineComponent({
        components: { UErrorContainer, UErrorItem, UStrongPassword, VerificationPrompt },
        setup() {
            const toast = useToast();
            const router = useRouter();
            const store = useStore();
            const { t } = useI18n();
            const loading = ref(false), saving = ref(false);
            const loadingIcon = computed(() => loading.value || saving.value
                ? "pi pi-spin pi-spinner" : "");
            const minPasswordLength = ref(DEFAULT_MIN_PASSWORD_LENGTH);
            const minPasswordScore = DEFAULT_MIN_PASSWORD_SCORE;
            const userId = ref(store.state.user?.id ?? "");

            const accountClient = new AccountClient();
            const account = reactive(new AccountDto());
            let secFactor: SecondFactorDto;
            const secondFactorMissing = ref(false);
            let verificationCodeSent = false;
            const secondFactorClient = new SecondFactorClient();
            const verifyPrompt = ref(null);
            const showVerifyPrompt = ref(false);
            const useSecondFactorVerification = ref(false);

            const reCaptcha = useReCaptcha();
            let recaptchaToken = "";
            const login = ref('');
            const displayModal = ref(false);

            const getRecaptchaToken = async () => {
                if (!store.getters.isRecaptchaEnabled) {
                    return true;
                }
                recaptchaToken = "";
                // (optional) Wait until recaptcha has been loaded.
                if (reCaptcha) {
                    await reCaptcha.recaptchaLoaded();
                    recaptchaToken = await reCaptcha.executeRecaptcha('resetPassword');
                }

                if (recaptchaToken == null || recaptchaToken == undefined || recaptchaToken == "") {
                    toast.add({
                        severity: 'error',
                        summary: t("toast.titles.error"),
                        detail: t("toast.messages.recaptchaError")
                    });
                    return false;
                }

                return true;
            }

            const form = reactive({
                origPass: "",
                newPass: "",
                newPassValid: false,
                newPassVerify: ""
            });

            useHead({
                meta: [
                    {
                        name: `robots`,
                        content: "noindex",
                    },
                ],
            });

            onMounted(async () => {
                try {
                    loading.value = true;
                    Object.assign(account, await accountClient.getMe());
                    login.value = account.username ? account.username : account.email;
                    if (account.type === "Service") {
                        minPasswordLength.value = SERVICE_ACCOUNT_MIN_PASSWORD_LENGTH;
                    } else {
                        minPasswordLength.value = DEFAULT_MIN_PASSWORD_LENGTH;
                    }

                    if (account.usePasswordConfirmation) {
                        useSecondFactorVerification.value = false;
                    } else {
                        useSecondFactorVerification.value = true;
                        secFactor = (await accountClient.getSecondFactors(userId.value))[0];
                        if (secFactor == null) {
                            secondFactorMissing.value = true;
                            displayModal.value = true;
                        }
                    }
                } catch {
                    // intercepted by axios
                }

                loading.value = false;
            });

            const passwordsMatch = () => form.newPass === form.newPassVerify; // TODO duplicated
            const rules = {
                origPass: { required },
                newPassVerify: { required, passwordsMatch }
            };

            const v$ = useVuelidate(rules, form);
            const canSubmit = computed(() => {
                return !loading.value
                    && !saving.value
                    && !secondFactorMissing.value
                    && !v$.value.$invalid
                    && form.newPassValid;
            });

            const updateNewPassVerify = (value: string | null | undefined) => {
                form.newPassVerify = (value ?? "").trim();
                v$.value.newPassVerify.$touch();
            };

            const updateOrigPass = (value: string | null | undefined) => {
                form.origPass = value ?? "";
                v$.value.origPass.$touch();
            };

            const authenticate = async () => {
                try {
                    if (!verificationCodeSent) {
                        await secondFactorClient.authenticate(secFactor.id, recaptchaToken)
                        Object(verifyPrompt.value).startTimer();
                        verificationCodeSent = true;
                    }
                    showVerifyPrompt.value = true;
                } catch {
                    // intercepted by axios
                }
            };

            const resendVerificationCode = async () => {
                if (!await getRecaptchaToken()) {
                    return;
                }
                verificationCodeSent = false;
                await authenticate();
            };

            const continueSaving = async (verificationData: string) => {
                saving.value = true;

                try {
                    const changePasswordDto = Object.assign(new ChangePasswordDto(), {
                        originalPassword: form.origPass,
                        newPassword: form.newPass,
                        verificationToken: verificationData
                    });

                    await accountClient.changePasswordVerify(
                        userId.value,
                        changePasswordDto);

                    if (useSecondFactorVerification.value) {
                        showVerifyPrompt.value = false;
                    }

                    toast.add({
                        severity: 'success',
                        detail: t("toast.messages.passwordChangeSuccess"),
                        life: 3000
                    });

                    router.push("user-profile");

                } catch {
                    // intercepted by axios
                }

                saving.value = false;

            };
            const changePassword = async (valid: boolean) => {

                v$.value.$touch();
                if (!valid) {
                    return;
                }

                saving.value = true;
                try {
                    if (useSecondFactorVerification.value) {
                        try {
                            const changePasswordValidateDto = Object.assign(new ChangePasswordValidateDto(), {
                                originalPassword: form.origPass,
                                newPassword: form.newPass
                            });

                            await accountClient.validateMyNewPassword(changePasswordValidateDto);

                            if (!await getRecaptchaToken()) {
                                return;
                            }
                            await authenticate();
                        }
                        catch {
                            // intercepted by axios
                        }
                    } else {
                        continueSaving("");
                    }

                } catch {
                    // intercepted by axios
                }

                saving.value = false;
            };
            return {
                form,
                v$,
                updateOrigPass,
                updateNewPassVerify,
                loading,
                saving,
                loadingIcon,
                canSubmit,
                changePassword,
                t,
                secondFactorMissing,
                showVerifyPrompt,
                verifyPrompt,
                continueSaving,
                resendVerificationCode,
                minPasswordLength,
                minPasswordScore,
                login,
                displayModal
            };
        },
    })
